import { tsv } from "@faire/slate/lib/tsv";
import { useEffect, useSyncExternalStore } from "react";

import BrandSideCart, {
  type BrandSideCartItem,
} from "../../../components/shared/BrandSideCart";

import {
  getNavigationHeaderBottom,
  getServerNavigationHeaderBottom,
  subscribeToNavigationHeaderPosition,
} from "./navigationHeaderPosition";

interface SavedProductsSideCartProps {
  open: boolean;
  brandName: string;
  minimum: number;
  items: BrandSideCartItem[];
  onChangeQuantity: (productToken: string, quantity: number) => void;
  onRemove: (productToken: string) => void;
  onViewCart: () => void;
  onClose: () => void;
}

/**
 * Brand side cart that sits beside the page content, as on the production
 * PDP and brand page: a sticky right column that reflows from 0 to its full
 * width while the cart slides in, so the body narrows rather than being
 * covered. Render it as a flex sibling of the content column. Stays mounted
 * so it can animate closed; Escape closes it.
 */
export const SavedProductsSideCart = ({
  open,
  brandName,
  minimum,
  items,
  onChangeQuantity,
  onRemove,
  onViewCart,
  onClose,
}: SavedProductsSideCartProps) => {
  const navigationBottom = useSyncExternalStore(
    subscribeToNavigationHeaderPosition,
    getNavigationHeaderBottom,
    getServerNavigationHeaderBottom
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  return (
    <aside
      aria-hidden={!open}
      className={columnStyle.cls({ open })}
      style={{
        top: navigationBottom,
        height: `calc(100vh - ${navigationBottom}px)`,
      }}
    >
      <div className={panelStyle.cls({ open })}>
        <BrandSideCart
          brandName={brandName}
          minimum={minimum}
          items={items}
          onChangeQuantity={onChangeQuantity}
          onRemove={onRemove}
          onViewCart={onViewCart}
          onClose={onClose}
          className="h-full"
        />
      </div>
    </aside>
  );
};

// Production PDP reflow: the column animates its width while the panel
// inside slides in from the right edge, both over 300ms ease-out.
const columnStyle = tsv({
  base: [
    "sticky shrink-0 overflow-x-clip overflow-y-hidden",
    "transition-[width] duration-300 ease-out",
  ],
  variants: {
    open: {
      true: "w-[320px] xl:w-[360px]",
      false: "pointer-events-none w-0",
    },
  },
});

const panelStyle = tsv({
  base: [
    "h-full w-[320px] xl:w-[360px]",
    "transition-transform duration-300 ease-out",
  ],
  variants: {
    open: {
      true: "translate-x-0",
      false: "translate-x-full",
    },
  },
});
